import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  effect,
  inject,
  OnDestroy,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import type WaveSurfer from 'wavesurfer.js';
import type RecordPlugin from 'wavesurfer.js/plugins/record';
import { AudioTranscodeService } from '../../../services/audio-transcode.service';

/**
 * Audio recorder using wavesurfer.js Record plugin.
 * Drop-in replacement for om-audio-recorder with same API (audioReady, audioCleared, title).
 */
@Component({
  selector: 'om-waveform-recorder',
  imports: [CommonModule],
  templateUrl: './waveform-recorder.html',
  styleUrl: './waveform-recorder.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WaveformRecorder implements AfterViewInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly transcodeService = inject(AudioTranscodeService);

  /** Optional title displayed above the waveform. */
  title = input<string>('');
  /** Waveform height in pixels. Default 72. */
  height = input<number>(72);
  /** Preload remote audio URL (useful for read mode / editing existing audio). */
  initialAudioUrl = input<string | null>(null);
  /** Read-only mode: playback only, hides record/upload/delete actions. */
  readOnly = input(false);
  /** Used to force a reset from the parent component. */
  resetKey = input<number>(0);
  audioReady = output<Blob>();
  audioCleared = output<void>();

  readonly waveformContainer = viewChild<ElementRef<HTMLElement>>('waveformContainer');

  readonly durationSeconds = signal(0);
  readonly currentTime = signal(0);
  readonly state = signal<'idle' | 'recording' | 'recorded' | 'playing' | 'paused'>('idle');

  readonly formattedDuration = computed(() => this.formatTime(this.durationSeconds()));
  readonly formattedCurrentTime = computed(() => this.formatTime(this.currentTime()));

  private wavesurfer: WaveSurfer | null = null;
  private recordPlugin: RecordPlugin | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private loadedAudioUrl: string | null = null;

  constructor() {
    effect(() => {
      const url = this.initialAudioUrl();
      void this.syncInitialAudio(url);
    });

    effect(() => {
      const key = this.resetKey();
      if (key > 0) {
        this.clearAudioInternal();
        this.cdr.markForCheck();
      }
    });
  }

  async ngAfterViewInit(): Promise<void> {
    this.cdr.detectChanges();
    // Defer init so the container has layout dimensions (avoids 0x0 canvas)
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
    await this.initWaveSurfer();
    await this.syncInitialAudio(this.initialAudioUrl());
  }

  ngOnDestroy(): void {
    this.destroyWaveSurfer();
  }

  private async initWaveSurfer(): Promise<void> {
    const el = this.waveformContainer()?.nativeElement;
    if (!el) return;
    // Wait for container to have dimensions (layout may not be ready yet)
    await this.waitForDimensions(el);

    const WaveSurferConstructor = (await import('wavesurfer.js')).default;
    const RecordPluginConstructor = (await import('wavesurfer.js/plugins/record')).default;

    const mimeType = this.transcodeService.getRecorderMimeType() || 'audio/webm';
    const record = RecordPluginConstructor.create({
      scrollingWaveform: true,
      renderRecordedAudio: true,
      mimeType,
      scrollingWaveformWindow: 5,
    }) as RecordPlugin;

    this.recordPlugin = record;

    record.on('record-start', () => {
      this.state.set('recording');
      this.durationSeconds.set(0);
    });

    record.on('record-progress', (duration: number) => {
      this.durationSeconds.set(duration / 1000);
    });

    record.on('record-end', (blob: Blob) => {
      this.state.set('recorded');
      this.durationSeconds.set(record.getDuration() / 1000);
      this.audioReady.emit(blob);
    });

    const h = this.height();
    this.wavesurfer = WaveSurferConstructor.create({
      container: el,
      waveColor: '#E07A5F',
      progressColor: '#c46050',
      cursorWidth: 2,
      barWidth: 3,
      barGap: 2,
      barRadius: 2,
      height: h,
      plugins: [record],
      interact: false,
    }) as WaveSurfer;

    this.wavesurfer.on('timeupdate', (time: number) => {
      this.currentTime.set(time);
    });

    this.wavesurfer.on('play', () => {
      this.state.set('playing');
    });

    this.wavesurfer.on('pause', () => {
      this.state.set('paused');
    });

    this.wavesurfer.on('finish', () => {
      this.state.set('recorded');
      this.currentTime.set(0);
    });

    // Redraw when container gets dimensions (e.g. was 0 at init)
    let lastWidth = el.getBoundingClientRect().width;
    this.resizeObserver = new ResizeObserver(() => {
      const w = el.getBoundingClientRect().width;
      if (this.wavesurfer && lastWidth === 0 && w > 0) {
        this.wavesurfer.setOptions({});
      }
      lastWidth = w;
    });
    this.resizeObserver.observe(el);
  }

  private destroyWaveSurfer(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.recordPlugin?.destroy();
    this.recordPlugin = null;
    this.wavesurfer?.destroy();
    this.wavesurfer = null;
  }

  async startRecording(): Promise<void> {
    if (this.readOnly()) return;
    if (!this.recordPlugin) return;
    try {
      await this.recordPlugin.startRecording();
    } catch (err) {
      console.error('Microphone access denied', err);
    }
  }

  stopRecording(): void {
    this.recordPlugin?.stopRecording();
  }

  play(): void {
    this.wavesurfer?.play();
  }

  pause(): void {
    this.wavesurfer?.pause();
  }

  async onFileUpload(event: Event): Promise<void> {
    if (this.readOnly()) return;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.state.set('recorded');
    this.audioReady.emit(file);

    const url = URL.createObjectURL(file);
    try {
      await this.wavesurfer?.load(url);
      const dur = this.wavesurfer?.getDuration();
      if (dur != null) this.durationSeconds.set(dur);
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  triggerUpload(): void {
    if (this.readOnly()) return;
    (document.getElementById('waveformRecorderFileInput') as HTMLInputElement)?.click();
  }

  clearAudio(): void {
    if (this.readOnly()) return;
    this.clearAudioInternal();
    this.audioCleared.emit();
  }

  private clearAudioInternal(): void {
    if (!this.wavesurfer) return;
    this.wavesurfer.empty();
    this.durationSeconds.set(0);
    this.currentTime.set(0);
    this.state.set('idle');
    this.loadedAudioUrl = null;
  }

  private async syncInitialAudio(url: string | null): Promise<void> {
    if (!this.wavesurfer) return;

    if (!url) {
      if (this.state() === 'idle' && !this.loadedAudioUrl && this.durationSeconds() === 0) return;
      this.clearAudioInternal();
      this.cdr.markForCheck();
      return;
    }

    if (this.loadedAudioUrl === url) return;

    try {
      await this.wavesurfer.load(url);
      this.durationSeconds.set(this.wavesurfer.getDuration() || 0);
      this.currentTime.set(0);
      this.state.set('recorded');
      this.loadedAudioUrl = url;
      this.cdr.markForCheck();
    } catch (err) {
      console.warn('Could not preload waveform audio', err);
    }
  }

  private waitForDimensions(el: HTMLElement, maxWait = 1500): Promise<void> {
    const start = Date.now();
    return new Promise((resolve) => {
      const check = () => {
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          resolve();
          return;
        }
        if (Date.now() - start > maxWait) resolve();
        else requestAnimationFrame(check);
      };
      requestAnimationFrame(check);
    });
  }

  private formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }
}
